import { useMemo } from "react";
import { motion } from "framer-motion";

// European wheel order (clockwise from 0)
const WHEEL_ORDER = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24,
  16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26,
];

const RED = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);

const SIZE = 500;
const CX = SIZE / 2;
const CY = SIZE / 2;
const OUTER = 236;
const INNER = 148;
const TEXT_R = 198;
const SEGMENTS = WHEEL_ORDER.length;
const ANGLE = (Math.PI * 2) / SEGMENTS;

const polar = (r, a) => ({
  x: CX + r * Math.cos(a),
  y: CY + r * Math.sin(a),
});

const wedgePath = (i) => {
  const a0 = i * ANGLE - Math.PI / 2;
  const a1 = (i + 1) * ANGLE - Math.PI / 2;
  const p0 = polar(OUTER, a0);
  const p1 = polar(OUTER, a1);
  const p2 = polar(INNER, a1);
  const p3 = polar(INNER, a0);
  return `M ${p0.x} ${p0.y} A ${OUTER} ${OUTER} 0 0 1 ${p1.x} ${p1.y} L ${p2.x} ${p2.y} A ${INNER} ${INNER} 0 0 0 ${p3.x} ${p3.y} Z`;
};

const pocketColor = (n) => {
  if (n === 0) return "#0fae4a";
  return RED.has(n) ? "#c41e2a" : "#121212";
};

const RouletteSvg = ({ rotation = 0, spinning = false }) => {
  const pockets = useMemo(
    () =>
      WHEEL_ORDER.map((num, i) => {
        const mid = (i + 0.5) * ANGLE - Math.PI / 2;
        const t = polar(TEXT_R, mid);
        const deg = (mid * 180) / Math.PI + 90;
        return { num, i, t, deg, fill: pocketColor(num) };
      }),
    []
  );

  return (
    <div className={`roulette-svg ${spinning ? "is-spinning" : ""}`}>
      <svg
        className="roulette-svg__shadow"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        aria-hidden
      >
        <ellipse cx={CX} cy={CY + 18} rx={220} ry={40} fill="rgba(0,0,0,0.45)" />
      </svg>

      <motion.svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="roulette-svg__wheel"
        animate={{ rotate: rotation }}
        transition={{
          duration: spinning ? 3.4 : 0,
          ease: [0.12, 0.75, 0.12, 1],
        }}
        role="img"
        aria-label="Interactive gaming roulette wheel"
      >
        <defs>
          <radialGradient id="woodRim" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#8b5a2b" />
            <stop offset="45%" stopColor="#5c3418" />
            <stop offset="100%" stopColor="#2a160a" />
          </radialGradient>
          <radialGradient id="goldHub" cx="40%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#ffe08a" />
            <stop offset="45%" stopColor="#d4a017" />
            <stop offset="100%" stopColor="#8a5a00" />
          </radialGradient>
          <linearGradient id="spokeGold" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f6d56a" />
            <stop offset="100%" stopColor="#b8860b" />
          </linearGradient>
          <filter id="softGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Outer wood rim */}
        <circle cx={CX} cy={CY} r={248} fill="url(#woodRim)" />
        <circle
          cx={CX}
          cy={CY}
          r={242}
          fill="none"
          stroke="#c9a227"
          strokeWidth="3"
          opacity="0.85"
        />
        <circle
          cx={CX}
          cy={CY}
          r={238}
          fill="none"
          stroke="#3a2010"
          strokeWidth="6"
        />

        {/* Number pockets */}
        {pockets.map(({ num, i, t, deg, fill }) => (
          <g key={`${num}-${i}`}>
            <path d={wedgePath(i)} fill={fill} stroke="#1a1a1a" strokeWidth="0.6" />
            <text
              x={t.x}
              y={t.y}
              fill="#fff"
              fontSize="13"
              fontWeight="700"
              fontFamily="Orbitron, sans-serif"
              textAnchor="middle"
              dominantBaseline="middle"
              transform={`rotate(${deg} ${t.x} ${t.y})`}
            >
              {num}
            </text>
          </g>
        ))}

        {/* Inner dark hub (spins with ring edge only) */}
        <circle cx={CX} cy={CY} r={INNER} fill="#0a0a0a" />
        <circle
          cx={CX}
          cy={CY}
          r={INNER}
          fill="none"
          stroke="url(#spokeGold)"
          strokeWidth="5"
        />
      </motion.svg>

      {/* Fixed center brand mark - does not spin */}
      <div className="roulette-svg__center-logo" aria-hidden>
        <img src="/favicon.ico" alt="" />
      </div>

      {/* Fixed pointer at top */}
      <div className="roulette-svg__pointer" aria-hidden>
        <svg viewBox="0 0 40 36" width="28" height="26">
          <polygon points="20,36 0,0 40,0" fill="#f97316" />
          <polygon points="20,28 8,4 32,4" fill="#ffb36b" />
        </svg>
      </div>
    </div>
  );
};

export default RouletteSvg;
export { WHEEL_ORDER };
